import React, { useCallback, useEffect, useRef, useState } from 'react';
import { MessageCircle, Plus, Search, Send } from 'lucide-react';

import employeeService from '../../services/EmployeeService';
import whatsappService from '../../services/WhatsappService';

const errorText = (error) =>
  error?.response?.data?.errorMessage || error?.response?.data?.message || 'Unable to load WhatsApp messages.';

const timestamp = (value) => (value ? new Date(value).toLocaleString() : '');
const phoneValue = (value) => (typeof value === 'object' ? value?.FreeFormNumber || '' : value || '');
const employeePhone = (employee) =>
  phoneValue(employee.PrimaryPhone) || phoneValue(employee.Mobile) || phoneValue(employee.Phone);
const employeeName = (employee) =>
  [employee.FirstName, employee.FamilyName].filter(Boolean).join(' ') ||
  employee.DisplayName ||
  employee.email ||
  'Employee';

const normalizeWhatsappNumber = (value) => {
  const digits = String(value || '').replace(/\D/g, '');
  if (/^04\d{8}$/.test(digits)) return `61${digits.slice(1)}`;
  if (/^4\d{8}$/.test(digits)) return `61${digits}`;
  return digits;
};

const participantId = (item) =>
  normalizeWhatsappNumber(
    item?.whatsappUserId || item?.participantId || item?.phoneNumber || item?.senderId || item?.recipientId || ''
  );

const messageBody = (message) => {
  const raw = message.rawMessage || {};
  return (
    message.body ||
    raw.text?.body ||
    raw.button?.text ||
    raw.interactive?.button_reply?.title ||
    raw.interactive?.list_reply?.title ||
    (message.messageType ? `[${message.messageType} message]` : 'WhatsApp notification')
  );
};

const messageBodyWithLinks = (message) =>
  messageBody(message)
    .split(/(Visit website:\s*https?:\/\/[^\s]+|https?:\/\/[^\s]+)/g)
    .map((part, index) => {
      const websiteLink = part.match(/^Visit website:\s*(https?:\/\/[^\s]+)$/);
      const url = websiteLink?.[1] || (/^https?:\/\//.test(part) ? part : null);

      return url ? (
        <a
          className="tw-inline-block tw-bg-[#1976d2] tw-text-white tw-px-4 tw-py-2 tw-my-1 tw-rounded-full tw-no-underline tw-text-sm tw-font-medium tw-break-all hover:tw-text-[#ffffff] hover:tw-opacity-90 tw-transition-colors"
          href={url}
          key={`${part}-${index}`}
          rel="noopener noreferrer"
          target="_blank"
        >
          {websiteLink ? 'Visit website' : part}
        </a>
      ) : (
        <React.Fragment key={`text-${index}`}>{part}</React.Fragment>
      );
    });

export default function WhatsappMessageInbox() {
  const [conversations, setConversations] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [selected, setSelected] = useState(null);
  const [messages, setMessages] = useState([]);
  const [search, setSearch] = useState('');
  const [draft, setDraft] = useState('');
  const [choosing, setChoosing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [cursor, setCursor] = useState(null);
  const [error, setError] = useState('');
  const selectionVersion = useRef(0);
  const bottom = useRef(null);

  const loadConversations = useCallback(async (initial = false) => {
    const rows = await whatsappService.conversations();
    setConversations(rows);
    if (initial) setSelected(rows[0] || null);
  }, []);

  const loadHistory = useCallback(async (id, before, version) => {
    setHistoryLoading(true);
    try {
      const page = await whatsappService.history(id, before);
      if (version !== selectionVersion.current) return;
      const pageMessages = Array.isArray(page) ? page : page.messages || [];
      setMessages((old) => (before ? [...pageMessages, ...old] : pageMessages));
      setCursor(Array.isArray(page) ? null : page.nextCursor || null);
    } catch (requestError) {
      if (version === selectionVersion.current) setError(errorText(requestError));
    } finally {
      if (version === selectionVersion.current) setHistoryLoading(false);
    }
  }, []);

  useEffect(() => {
    loadConversations(true)
      .catch((requestError) => setError(errorText(requestError)))
      .finally(() => setLoading(false));
  }, [loadConversations]);

  useEffect(() => {
    const version = ++selectionVersion.current;
    const id = selected && participantId(selected);
    setMessages([]);
    setCursor(null);
    if (id) loadHistory(id, null, version);
  }, [selected, loadHistory]);

  useEffect(() => {
    bottom.current?.scrollIntoView({ block: 'nearest' });
  }, [messages.length]);

  async function newChat() {
    setChoosing(true);
    setSearch('');
    setError('');
    setLoading(true);
    try {
      const response = await employeeService.getEmployees();
      setEmployees((response?.data?.data || []).filter((employee) => employee.portalAccess === true));
    } catch (requestError) {
      setError(errorText(requestError));
    } finally {
      setLoading(false);
    }
  }

  function selectRow(row) {
    const isEmployee = choosing;
    setSelected(
      isEmployee
        ? {
            ...row,
            whatsappUserId: employeePhone(row),
            phoneNumber: employeePhone(row),
            displayName: employeeName(row)
          }
        : row
    );
    setChoosing(false);
    setSearch('');
    setDraft('');
    setError('');
  }

  async function send(event) {
    event.preventDefault();
    const id = selected && participantId(selected);
    const displayName = selected?.displayName;
    const body = draft.trim();
    if (!id || !body || !displayName || sending) return;
    setSending(true);
    setError('');
    try {
      await whatsappService.send(id, displayName, body);
      setDraft('');
      await loadConversations();
      await loadHistory(id, null, selectionVersion.current);
    } catch (requestError) {
      setError(errorText(requestError));
    } finally {
      setSending(false);
    }
  }

  const query = search.toLowerCase().trim();
  const rows = (choosing ? employees : conversations).filter((row) => {
    const name = choosing ? employeeName(row) : row.displayName || row.contactName || '';
    const phone = choosing ? employeePhone(row) : participantId(row);
    return `${name} ${phone}`.toLowerCase().includes(query);
  });
  const selectedName = selected?.displayName || selected?.contactName || participantId(selected);

  return (
    <section className="tw-p-6 tw-text-[#243343] max-[700px]:tw-p-3" aria-label="WhatsApp inbox">
      <h2 className="tw-flex tw-items-center tw-gap-2.5 tw-text-2xl tw-mb-5">
        <MessageCircle size={24} /> WhatsApp messages
      </h2>
      {error && (
        <div role="alert" className="tw-bg-[#fff0ee] tw-text-[#9b3024] tw-p-3 tw-mb-3 tw-rounded-md">
          {error}
        </div>
      )}

      <div className="tw-grid tw-grid-cols-[40%_60%] tw-h-[72vh] tw-min-h-[480px] tw-border tw-border-solid tw-border-[#dce3e9] tw-rounded-xl tw-overflow-hidden tw-bg-white max-[700px]:tw-grid-cols-1 max-[700px]:tw-h-auto">
        <div className="tw-flex tw-flex-col tw-min-w-0 tw-min-h-0 tw-border-0 tw-border-r tw-border-solid tw-border-[#dce3e9] max-[700px]:tw-h-[330px] max-[700px]:tw-border-r-0 max-[700px]:tw-border-b">
          <div className="tw-p-[18px] tw-border-0 tw-border-b tw-border-solid tw-border-[#e5eaf0]">
            <label className="tw-flex tw-items-center tw-gap-2 tw-font-semibold tw-mb-2" htmlFor="whatsapp-search">
              <Search size={18} /> {choosing ? 'Active employees' : 'Message history'}
            </label>
            <input
              className="tw-w-full tw-p-2.5 tw-border tw-border-solid tw-border-[#cad4df] tw-rounded-md"
              id="whatsapp-search"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search name or phone number"
            />
          </div>
          <div className="tw-flex-1 tw-overflow-y-auto" aria-busy={loading}>
            {loading ? (
              <p className="tw-p-6 tw-text-center tw-text-[#64748b]">Loading…</p>
            ) : (
              rows.map((row) => {
                const id = choosing ? String(row._id) : participantId(row);
                const name = choosing ? employeeName(row) : row.displayName || row.contactName || id;
                const phone = choosing ? employeePhone(row) : id;
                return (
                  <button
                    type="button"
                    key={id}
                    disabled={sending || (choosing && !phone)}
                    className={`tw-flex tw-flex-col tw-gap-1 tw-w-full tw-text-left tw-border-0 tw-border-b tw-border-solid tw-border-[#edf0f4] tw-py-4 tw-px-[18px] tw-text-inherit hover:tw-bg-[#edf5fb] tw-break-words disabled:tw-opacity-50 disabled:tw-cursor-not-allowed focus-visible:tw-outline focus-visible:tw-outline-[3px] focus-visible:tw-outline-[#60a5fa] focus-visible:tw-outline-offset-[-3px] ${
                      !choosing && participantId(selected || {}) === id ? 'tw-bg-[#edf5fb]' : 'tw-bg-white'
                    }`}
                    onClick={() => selectRow(row)}
                  >
                    <strong>{name}</strong>
                    <span>{phone || 'No phone number'}</span>
                    {!choosing && (
                      <>
                        <span className="tw-max-w-full tw-truncate">{messageBody(row)}</span>
                        <small className="tw-text-[#526579] tw-text-xs">
                          {timestamp(row.statusTimestamp || row.createdAt)}
                        </small>
                      </>
                    )}
                  </button>
                );
              })
            )}
            {!loading && !rows.length && (
              <p className="tw-p-6 tw-text-center tw-text-[#64748b]">
                {choosing ? 'No matching active employees.' : 'No conversations yet. Start a new chat.'}
              </p>
            )}
          </div>
          <div className="tw-p-3.5 tw-flex tw-flex-wrap tw-gap-2 tw-border-0 tw-border-t tw-border-solid tw-border-[#e5eaf0]">
            {choosing && (
              <button
                className="tw-inline-flex tw-items-center tw-justify-center tw-gap-2 tw-border-0 tw-py-2.5 tw-px-3.5 tw-rounded-md tw-bg-[#185c88] tw-text-white"
                type="button"
                onClick={() => {
                  setChoosing(false);
                  setSearch('');
                }}
              >
                Back to history
              </button>
            )}
            <button
              className="tw-inline-flex tw-items-center tw-justify-center tw-gap-2 tw-border-0 tw-py-2.5 tw-px-3.5 tw-rounded-md tw-bg-[#185c88] tw-text-white disabled:tw-opacity-50"
              type="button"
              disabled={sending || loading}
              onClick={newChat}
            >
              <Plus size={18} /> {choosing ? 'Refresh employees' : 'New chat'}
            </button>
            {!choosing && (
              <button
                className="tw-inline-flex tw-items-center tw-justify-center tw-gap-2 tw-border-0 tw-py-2.5 tw-px-3.5 tw-rounded-md tw-bg-[#185c88] tw-text-white disabled:tw-opacity-50"
                type="button"
                disabled={loading || sending}
                onClick={() => loadConversations().catch((requestError) => setError(errorText(requestError)))}
              >
                Refresh history
              </button>
            )}
          </div>
        </div>

        <div className="tw-flex tw-flex-col tw-min-w-0 tw-min-h-0 max-[700px]:tw-h-[550px]">
          {selected ? (
            <>
              <header className="tw-flex tw-flex-col tw-gap-1 tw-p-[18px] tw-border-0 tw-border-b tw-border-solid tw-border-[#e5eaf0]">
                <strong>{selectedName}</strong>
                <span>{participantId(selected)}</span>
              </header>
              <div className="tw-flex-1 tw-overflow-y-auto tw-p-5 tw-bg-[#f6f8fb]" aria-busy={historyLoading}>
                {cursor && (
                  <button
                    className="tw-inline-flex tw-items-center tw-justify-center tw-gap-2 tw-border-0 tw-py-2.5 tw-px-3.5 tw-rounded-md tw-bg-[#185c88] tw-text-white disabled:tw-opacity-50"
                    type="button"
                    disabled={historyLoading || sending}
                    onClick={() => loadHistory(participantId(selected), cursor, selectionVersion.current)}
                  >
                    Load older messages
                  </button>
                )}
                {historyLoading && <p className="tw-p-6 tw-text-center tw-text-[#64748b]">Loading history…</p>}
                {!historyLoading && !messages.length && (
                  <p className="tw-p-6 tw-text-center tw-text-[#64748b]">No messages with {selectedName} yet.</p>
                )}
                {messages.map((message) => {
                  const inbound = message.direction === 'inbound';
                  return (
                    <article
                      className={`tw-w-fit tw-max-w-[88%] tw-m-0 tw-mb-4 tw-py-3 tw-px-4 tw-rounded-xl tw-break-words ${
                        inbound
                          ? ' tw-mr-auto tw-bg-white tw-rounded-bl-[2px]'
                          : 'tw-ml-auto tw-bg-[#dcf8c6] tw-rounded-br-[2px]'
                      }`}
                      key={message._id || message.messageId}
                    >
                      <p className="tw-whitespace-pre-wrap tw-m-0 tw-mb-2">{messageBodyWithLinks(message)}</p>
                      <small className="tw-block tw-text-[#526579] tw-text-xs">
                        {timestamp(message.statusTimestamp || message.createdAt)}
                        {message.status ? ` · ${message.status}` : ''}
                      </small>
                    </article>
                  );
                })}
                <div ref={bottom} />
              </div>
              <form className="tw-p-[18px] tw-border-0 tw-border-t tw-border-solid tw-border-[#e5eaf0]" onSubmit={send}>
                <label className="tw-flex tw-items-center tw-gap-2 tw-font-semibold tw-mb-2" htmlFor="whatsapp-message">
                  WhatsApp message from Boxshall Group
                </label>
                <textarea
                  className="tw-w-full tw-p-2.5 tw-border tw-border-solid tw-border-[#cad4df] tw-rounded-md tw-resize-y tw-max-h-[180px]"
                  id="whatsapp-message"
                  rows={3}
                  maxLength={1600}
                  value={draft}
                  disabled={sending}
                  onChange={(event) => setDraft(event.target.value)}
                  placeholder="Write a WhatsApp message…"
                />
                <div className="tw-flex tw-justify-between tw-items-center tw-gap-3 tw-mt-2">
                  <small className="tw-text-[#526579] tw-text-xs">{draft.length}/1600</small>
                  <button
                    className="tw-inline-flex tw-items-center tw-justify-center tw-gap-2 tw-border-0 tw-py-2.5 tw-px-3.5 tw-rounded-md tw-bg-[#185c88] tw-text-white disabled:tw-opacity-50"
                    type="submit"
                    disabled={sending || historyLoading || !participantId(selected) || !draft.trim()}
                  >
                    <Send size={17} /> {sending ? 'Sending…' : 'Send WhatsApp'}
                  </button>
                </div>
              </form>
            </>
          ) : (
            <p className="tw-p-6 tw-text-center tw-text-[#64748b]">Select a conversation or start a new chat.</p>
          )}
        </div>
      </div>
    </section>
  );
}
